import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Grid, Button, Skeleton, Alert, Breadcrumbs, Link, IconButton, TextField,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation, Trans } from 'react-i18next';
import {
  needsPartyPerson, personalGuestName, pickLocaleName, pickLocalePath, restrictGuestText,
  type GuestGroup, type GuestIdentity, type GuestPublicInfo, type GuestPublicMember,
} from '@wedding/shared';
import NameText from '../../../shared/src/components/NameText';
import EntityIconMark from '../../../shared/src/components/icons/EntityIconMark';

interface PersonList {
  partyName: string | null;
  partyNameZh?: string | null;
  members: Array<{ id: number; name: string | null; nameZh?: string | null; icon?: GuestPublicMember['icon'] }>;
}

const apiBase = '/api';

const SIDE_COLORS = {
  bride: { border: '#e91e63', bg: 'rgba(233,30,99,0.06)', text: '#c2185d' },
  groom: { border: '#1565c0', bg: 'rgba(21,101,192,0.06)', text: '#0d47a1' },
  neutral: { border: 'divider', bg: 'transparent', text: 'text.primary' },
};

function findGroupPath(nodes: GuestGroup[], groupId: number, trail: GuestGroup[] = []): GuestGroup[] | null {
  for (const node of nodes) {
    const next = [...trail, node];
    if (node.id === groupId) return next;
    const found = findGroupPath(node.children ?? [], groupId, next);
    if (found) return found;
  }
  return null;
}

function searchable(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function nameMatches(query: string, ...parts: Array<string | null | undefined>): boolean {
  const needle = searchable(query);
  if (!needle) return false;
  return parts.some((part) => searchable(part).includes(needle));
}

type SearchHit =
  | { kind: 'party'; guest: GuestPublicInfo }
  | { kind: 'member'; guest: GuestPublicInfo; member: GuestPublicMember };

export default function IdentifyPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isChange = searchParams.get('change') === '1';
  const returnTo = searchParams.get('return');

  const [tree, setTree] = useState<GuestGroup[]>([]);
  const [guests, setGuests] = useState<GuestPublicInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentGuest, setCurrentGuest] = useState<GuestIdentity | null>(null);
  const [path, setPath] = useState<GuestGroup[]>([]);
  const [personList, setPersonList] = useState<PersonList | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState('');
  const [queryAdjusted, setQueryAdjusted] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        await fetch(`${apiBase}/auth/session`, { method: 'POST', credentials: 'include' });

        const meRes = await fetch(`${apiBase}/guests/me`, { credentials: 'include' });
        let me: GuestIdentity | null = null;
        if (meRes.ok) {
          const meJson = await meRes.json();
          me = meJson.data as GuestIdentity;
          setCurrentGuest(me);
          if (!isChange && !needsPartyPerson(me)) {
            navigate('/', { replace: true });
            return;
          }
        }

        const [treeRes, guestsRes] = await Promise.all([
          fetch(`${apiBase}/guest-groups/tree`, { credentials: 'include' }),
          fetch(`${apiBase}/guests/public`, { credentials: 'include' }),
        ]);
        const treeJson = await treeRes.json();
        const guestsJson = await guestsRes.json();
        const nextTree = (treeJson.data ?? []) as GuestGroup[];
        setTree(nextTree);
        setGuests(guestsJson.data ?? []);

        const partyWithPeople = !!me && me.effectiveMode === 'party' && (me.members?.length ?? 0) > 0;
        if (me && partyWithPeople && (isChange || needsPartyPerson(me))) {
          if (me.groupId != null) {
            const groupPath = findGroupPath(nextTree, me.groupId);
            if (groupPath) setPath(groupPath);
          }
          setPersonList({ partyName: me.name, partyNameZh: me.nameZh, members: me.members ?? [] });
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate, isChange]);

  const searching = query.trim().length > 0;
  const hits = useMemo<SearchHit[]>(() => {
    if (!searching) return [];
    const found: SearchHit[] = [];
    for (const guest of guests) {
      if (nameMatches(query, guest.name, guest.nameZh)) found.push({ kind: 'party', guest });
      for (const member of guest.members ?? []) {
        if (nameMatches(query, member.name, member.nameZh)) found.push({ kind: 'member', guest, member });
      }
    }
    return found;
  }, [guests, query, searching]);

  const current = path.length > 0 ? path[path.length - 1] : null;
  const childGroups = current ? (current.children ?? []) : tree;
  const currentGuests = guests.filter((g) => g.groupId === (current?.id ?? null));

  const finish = () => {
    if (returnTo) {
      window.location.href = returnTo;
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleSelect = async (guest: GuestPublicInfo, partyMemberId?: number) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/guests/identify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(partyMemberId ? { guestId: guest.id, partyMemberId } : { guestId: guest.id }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to identify');
      }
      const identified = json.data as GuestIdentity;
      setCurrentGuest(identified);
      if (!partyMemberId && identified.effectiveMode === 'party' && (identified.members?.length ?? 0) > 0) {
        setQuery('');
        setPersonList({ partyName: guest.name, partyNameZh: guest.nameZh, members: identified.members ?? [] });
        setSubmitting(false);
        return;
      }
      finish();
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  };

  const handleSelectMember = async (memberId: number) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/guests/me/member`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ partyMemberId: memberId }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to identify');
      }
      finish();
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 8 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4 }}>
          <Skeleton variant="text" width="55%" height={52} sx={{ mx: 'auto', mb: 1 }} />
          <Skeleton variant="text" width="75%" height={28} sx={{ mx: 'auto', mb: 3 }} />
          <Grid container spacing={1.5}>
            {[0, 1, 2, 3].map((i) => (
              <Grid key={i} size={{ xs: 6 }}>
                <Skeleton variant="rounded" height={64} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 8 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4, mt: 2 }}>
        <Typography variant="h4" textAlign="center" gutterBottom>
          {t('identify.title')}
        </Typography>
        <Typography textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
          {t('identify.subtitle')}
        </Typography>

        <TextField
          fullWidth
          value={query}
          onChange={(event) => {
            const next = restrictGuestText(event.target.value);
            setQueryAdjusted(next !== event.target.value);
            setQuery(next);
          }}
          placeholder={t('identify.searchPlaceholder')}
          helperText={queryAdjusted ? t('common.inputAdjusted') : undefined}
          sx={{ mb: 2 }}
        />

        {currentGuest && isChange && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Trans i18nKey="identify.currentlyIdentified" values={{ name: personalGuestName(currentGuest, i18n.language) }}>
              You are currently identified as <strong>{{ name: currentGuest.name } as any}</strong>
            </Trans>
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {searching && (
          hits.length > 0 ? (
            <Grid container spacing={1}>
              {hits.map((hit) => {
                const guest = hit.guest;
                const member = hit.kind === 'member' ? hit.member : null;
                const label = member
                  ? pickLocaleName(member.name, member.nameZh, i18n.language)
                  : pickLocaleName(guest.name, guest.nameZh, i18n.language);
                const pathLabel = pickLocalePath(guest.groupPath, guest.groupPathZh, i18n.language);
                const partyLabel = member ? pickLocaleName(guest.name, guest.nameZh, i18n.language) : '';
                const secondary = [partyLabel, pathLabel].filter(Boolean).join(' · ');
                return (
                  <Grid key={member ? `m-${member.id}` : `g-${guest.id}`} size={{ xs: 12 }}>
                    <Button
                      fullWidth variant="contained"
                      onClick={() => handleSelect(guest, member?.id)}
                      disabled={submitting}
                      sx={{ py: 1.5, textTransform: 'none', justifyContent: 'flex-start', alignItems: 'flex-start' }}
                    >
                      <Box sx={{ textAlign: 'left' }}>
                        <NameText name={label} icon={member?.icon ?? guest.icon} />
                        {secondary && (
                          <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-line', opacity: 0.85 }}>
                            {secondary}
                          </Typography>
                        )}
                      </Box>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography textAlign="center" color="text.secondary">
              {t('identify.noSearchResults')}
            </Typography>
          )
        )}

        {!searching && (path.length > 0 || personList) && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => {
                if (personList) setPersonList(null);
                else setPath(path.slice(0, -1));
              }}
              sx={{ mr: 0.5 }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Breadcrumbs sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
              <Link component="button" underline="hover" onClick={() => { setPersonList(null); setPath([]); }} sx={{ fontSize: 'inherit' }}>{t('common.all')}</Link>
              {path.map((g, i) =>
                personList || i < path.length - 1 ? (
                  <Link key={g.id} component="button" underline="hover" onClick={() => { setPersonList(null); setPath(path.slice(0, i + 1)); }} sx={{ fontSize: 'inherit', whiteSpace: 'pre-line' }}>{pickLocaleName(g.name, g.nameZh, i18n.language)}</Link>
                ) : (
                  <Typography key={g.id} component="span" color="text.primary" sx={{ fontSize: 'inherit' }}>
                    <NameText name={pickLocaleName(g.name, g.nameZh, i18n.language)} icon={g.icon} />
                  </Typography>
                ),
              )}
              {personList && (
                <Typography component="span" color="text.primary" sx={{ fontSize: 'inherit' }}>
                  <NameText name={pickLocaleName(personList.partyName, personList.partyNameZh, i18n.language)} />
                </Typography>
              )}
            </Breadcrumbs>
          </Box>
        )}

        {!searching && personList && (
          <Grid container spacing={1}>
            {personList.members.map((member) => (
              <Grid key={member.id} size={{ xs: 12 }}>
                <Button
                  fullWidth variant="contained"
                  onClick={() => handleSelectMember(member.id)}
                  disabled={submitting}
                  sx={{ py: 1.5, textTransform: 'none', justifyContent: 'flex-start' }}
                >
                  <NameText name={pickLocaleName(member.name, member.nameZh, i18n.language)} icon={member.icon} />
                </Button>
              </Grid>
            ))}
          </Grid>
        )}

        {!searching && !personList && childGroups.length > 0 && (
          <Grid container spacing={1.5} sx={{ mb: 2, justifyContent: 'center' }}>
            {childGroups.map((group) => {
              const colors = group.side ? SIDE_COLORS[group.side] : SIDE_COLORS.neutral;
              const label = pickLocaleName(group.name, group.nameZh, i18n.language);
              return (
                <Grid key={group.id} size={{ xs: 6 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setPath([...path, group])}
                    sx={{
                      py: 2,
                      textTransform: 'none',
                      borderColor: colors.border,
                      bgcolor: colors.bg,
                      color: colors.text,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minHeight: 64,
                      '&:hover': {
                        borderColor: colors.border,
                        bgcolor: group.side === 'bride' ? 'rgba(233,30,99,0.12)' : group.side === 'groom' ? 'rgba(21,101,192,0.12)' : undefined,
                      },
                    }}
                  >
                    {label.includes('\n') ? (
                      <NameText name={label} icon={group.icon} sx={{ flexDirection: 'column', alignItems: 'center' }} />
                    ) : (
                      <GroupLabel name={label} icon={group.icon} />
                    )}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        )}

        {!searching && !personList && currentGuests.length > 0 && (
          <Grid container spacing={1}>
            {currentGuests.map((guest) => (
              <Grid key={guest.id} size={{ xs: 12 }}>
                <Button
                  fullWidth variant="contained"
                  onClick={() => handleSelect(guest)}
                  disabled={submitting}
                  sx={{ py: 1.5, textTransform: 'none', justifyContent: 'flex-start' }}
                >
                  <NameText name={pickLocaleName(guest.name, guest.nameZh, i18n.language)} icon={guest.icon} />
                </Button>
              </Grid>
            ))}
          </Grid>
        )}

        {!searching && !personList && childGroups.length === 0 && currentGuests.length === 0 && (
          <Typography textAlign="center" color="text.secondary">
            {t('identify.noGuests')}
          </Typography>
        )}
      </Paper>
    </Container>
  );
}

function GroupLabel({ name, icon }: { name: string; icon: GuestGroup['icon'] }) {
  const match = name.match(/^((?:Bride'?s?|Groom'?s?|新娘|新郎)\s*)/i);
  const prefix = match?.[1]?.trim() ?? '';
  const rest = match ? name.slice(match[1]!.length).trim() : '';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <EntityIconMark icon={icon} />
      {prefix && rest ? (
        <>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.2 }}>{prefix}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{rest}</Typography>
        </>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
      )}
    </Box>
  );
}
